import { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { useLanguage } from '@/App';
import { API } from '@/App';
import { ChevronDown } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { seoData } from '@/utils/seoData';

const FAQPage = () => {
  const { language } = useLanguage();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const content = {
    tr: {
      title: 'Sıkça Sorulan Sorular',
      subtitle: 'Merak ettiklerinizin cevaplarını burada bulabilirsiniz',
      loading: 'Yükleniyor...',
      all: 'Tümü',
    },
    en: {
      title: 'Frequently Asked Questions',
      subtitle: 'Find answers to your questions here',
      loading: 'Loading...',
      all: 'All',
    },
  };

  const t = content[language];

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const response = await axios.get(`${API}/faq`);
        setFaqs(response.data);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFAQs();
  }, []);

  const categories = ['all', ...new Set(faqs.map(faq => faq.category))];
  const filteredFaqs = selectedCategory === 'all' ? faqs : faqs.filter(faq => faq.category === selectedCategory);

  const pageSeo = seoData.faq[language];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={pageSeo.title}
        description={pageSeo.description}
        keywords={pageSeo.keywords}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            data-testid="faq-title"
          >
            {t.title}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600" data-testid="faq-subtitle">{t.subtitle}</p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`faq-category-${category}`}
              >
                {category === 'all' ? t.all : category}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center text-gray-600">{t.loading}</div>
          ) : (
            <Accordion type="single" collapsible className="space-y-4">
              {filteredFaqs.map((faq, index) => (
                <AccordionItem 
                  key={faq.id} 
                  value={`item-${index}`}
                  className="bg-white border border-gray-200 rounded-lg px-6"
                  data-testid={`faq-item-${faq.id}`}
                >
                  <AccordionTrigger className="text-left hover:no-underline py-4">
                    <span className="text-lg font-semibold text-gray-900">
                      {language === 'tr' ? faq.question_tr : faq.question_en}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 pb-4">
                    {language === 'tr' ? faq.answer_tr : faq.answer_en}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;